/**
 * DesignerToolRegistry — process-wide singleton holding every
 * registered DesignerTool.
 *
 * Phase 13 P0 (June 2026)
 * =======================
 *
 * Why a singleton
 * ---------------
 *
 * Tool registration happens at app boot (see `bootstrap.js`) and
 * the same registry is consulted from every designer page render.
 * A singleton keeps the registry's identity stable across React
 * renders so memoization (e.g. `useMemo(() => registry.listFor(ctx))`)
 * stays effective. The registry is intentionally NOT React state —
 * tools are static metadata; they don't change while a coach is
 * editing.
 *
 * Filtering pipeline
 * ------------------
 *
 * `listFor(ctx)` runs every registered tool through three filters,
 * in order:
 *
 *   1. **Feature flag.** Tools with `feature_flag` set are dropped
 *      unless `isFlagOn(flag, ctx)` returns truthy. The framework
 *      ships a default `isFlagOn` (reads `localStorage` keys with
 *      a `flowgenix.flag.` prefix) but the registry accepts an
 *      injected resolver for testability and per-app override.
 *
 *   2. **RBAC.** Tools with non-empty `rbac` are dropped unless the
 *      caller's role set (from `ctx.userRoles`) intersects the
 *      tool's required roles. Empty `rbac` (default) = any coach.
 *
 *   3. **applies_to.** The tool's predicate is consulted last.
 *      Predicates are pure & cheap, but feature_flag + RBAC
 *      filtering happens FIRST so we don't waste cycles evaluating
 *      predicates for tools the user can't see anyway.
 *
 * The result is sorted by `weight` ascending, then label
 * alphabetically — gives a deterministic, reviewable rendering
 * order regardless of registration sequence.
 *
 * Duplicate id → registration throws. There is no
 * "last-registration-wins" mode; conflicts are bugs we want to
 * surface immediately.
 *
 * @see DesignerTool — the spec contract for what registers
 * @see DesignerShell — calls listForSlot(ctx, slot) once per slot
 */

import { DESIGNER_SLOTS } from './DesignerTool';

// ────────────────────────────────────────────────────────────────────
// Default feature-flag resolver.
// ────────────────────────────────────────────────────────────────────

/**
 * Default resolver: reads `localStorage[flowgenix.flag.<flag>]`. The
 * framework's `bootstrap.js` writes the per-shell defaults at boot,
 * so this resolver is the source of truth in production. Tests can
 * inject a stub via `setFeatureFlagResolver()`.
 *
 * @param {string} flag
 * @returns {boolean}
 */
function defaultIsFlagOn(flag) {
  if (!flag) return true;
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage?.getItem(`flowgenix.flag.${flag}`);
    return v === '1' || v === 'true' || v === 'on';
  } catch (_) {
    return false;
  }
}

// ────────────────────────────────────────────────────────────────────
// Registry implementation.
// ────────────────────────────────────────────────────────────────────

/**
 * @internal
 *
 * The class is exported only so unit tests can instantiate fresh
 * registries without polluting the singleton. App code uses the
 * named export `registry` (the singleton) or the small surface of
 * `register`/`listFor`/etc. exported below.
 */
export class _DesignerToolRegistry {
  constructor() {
    /** @type {Map<string, import('./DesignerTool').DesignerTool>} */
    this._byId = new Map();
    this._isFlagOn = defaultIsFlagOn;
  }

  /**
   * Register a tool. Throws on duplicate id or invalid spec.
   * @param {import('./DesignerTool').DesignerTool} tool
   */
  register(tool) {
    if (!tool || typeof tool !== 'object' || !tool.id) {
      throw new Error(
        'DesignerToolRegistry.register: argument must be a DesignerTool ' +
        'instance with an id (got: ' + JSON.stringify(tool) + ')',
      );
    }
    if (this._byId.has(tool.id)) {
      throw new Error(
        `DesignerToolRegistry.register: duplicate tool id ${JSON.stringify(tool.id)}. ` +
        'Tool ids must be globally unique. ' +
        '(Hint: bootstrap.js may be running twice, or a domain pack ' +
        'is shadowing a platform tool — rename the domain tool.)',
      );
    }
    this._byId.set(tool.id, tool);
    return tool;
  }

  /**
   * Bulk register convenience — same throw semantics as `register`.
   * @param {Iterable<import('./DesignerTool').DesignerTool>} tools
   */
  registerAll(tools) {
    for (const t of tools) this.register(t);
  }

  /** Test-only: clear every registration. */
  reset() {
    this._byId.clear();
    this._isFlagOn = defaultIsFlagOn;
  }

  /**
   * Override the feature-flag resolver. Used by `bootstrap.js` to
   * wire a real flag service, and by unit tests to fake flag state.
   * @param {(flag: string, ctx?: any) => boolean} fn
   */
  setFeatureFlagResolver(fn) {
    this._isFlagOn = typeof fn === 'function' ? fn : defaultIsFlagOn;
  }

  /** @returns {import('./DesignerTool').DesignerTool[]} every registered tool, no filters */
  all() {
    return [...this._byId.values()];
  }

  /** @param {string} id */
  get(id) {
    return this._byId.get(id) || null;
  }

  /** @param {string} id */
  has(id) {
    return this._byId.has(id);
  }

  /** @returns {number} */
  get size() {
    return this._byId.size;
  }

  /**
   * Return the ordered list of tools that should mount given the
   * current designer context. See module docstring for the filter
   * pipeline.
   *
   * @param {object} ctx                                    The DesignerContextValue.
   * @param {string[]=} ctx.userRoles                       Caller's role claims.
   * @returns {import('./DesignerTool').DesignerTool[]}
   */
  listFor(ctx) {
    const list = this.all()
      .filter((t) => !t.feature_flag || this._isFlagOn(t.feature_flag, ctx))
      .filter((t) => roleAllowsTool(ctx?.userRoles, t.rbac))
      .filter((t) => evaluateApplies(t, ctx));

    // Stable sort: weight asc, label localeCompare.
    list.sort((a, b) => {
      const dw = (a.weight ?? 100) - (b.weight ?? 100);
      if (dw !== 0) return dw;
      return String(a.label).localeCompare(String(b.label));
    });
    return list;
  }

  /**
   * Convenience: tools for one slot. The shell calls this once per
   * slot per render.
   *
   * @param {object} ctx
   * @param {import('./DesignerTool').DesignerSlot} slot
   */
  listForSlot(ctx, slot) {
    return this.listFor(ctx).filter((t) => t.slot === slot);
  }

  /**
   * Group `listFor()` output by slot. Returned object always
   * contains a (possibly empty) array for every canonical slot, so
   * the shell can map without nullish guards.
   *
   * @param {object} ctx
   * @returns {Record<import('./DesignerTool').DesignerSlot, import('./DesignerTool').DesignerTool[]>}
   */
  listBySlot(ctx) {
    const all = this.listFor(ctx);
    /** @type {any} */
    const out = {};
    for (const s of DESIGNER_SLOTS) out[s] = [];
    for (const t of all) {
      out[t.slot].push(t);
    }
    return out;
  }
}

// ────────────────────────────────────────────────────────────────────
// Helpers.
// ────────────────────────────────────────────────────────────────────

/**
 * RBAC gate. Empty `requiredRoles` → anyone can use the tool.
 *
 * @param {string[]=} userRoles
 * @param {string[]=} requiredRoles
 * @returns {boolean}
 */
function roleAllowsTool(userRoles, requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  const have = new Set(userRoles || []);
  return requiredRoles.some((r) => have.has(r));
}

/**
 * Run the tool's `applies_to` predicate. The predicate itself wraps
 * its body in a `safe()` (see predicates.js) so an exception here
 * already returns false; this extra try/catch is belt-and-braces
 * for direct-class-instantiation tools that bypassed the helpers.
 *
 * @param {import('./DesignerTool').DesignerTool} tool
 * @param {any} ctx
 */
function evaluateApplies(tool, ctx) {
  try {
    return Boolean(tool.applies_to?.(ctx));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[designer.registry] tool ${tool.id} applies_to threw:`, err);
    return false;
  }
}

// ────────────────────────────────────────────────────────────────────
// Module-level singleton + convenience surface.
// ────────────────────────────────────────────────────────────────────

/** Process-wide singleton. */
export const registry = new _DesignerToolRegistry();

export const register = (tool) => registry.register(tool);
export const registerAll = (tools) => registry.registerAll(tools);
export const listFor = (ctx) => registry.listFor(ctx);
export const listForSlot = (ctx, slot) => registry.listForSlot(ctx, slot);
export const listBySlot = (ctx) => registry.listBySlot(ctx);
export const setFeatureFlagResolver = (fn) => registry.setFeatureFlagResolver(fn);

export default registry;
